import { Injectable } from '@angular/core';
import {
    GeoFileImportStatusContract,
    GeoFileUploadContract,
    GeoFileUploadGeofenceContract,
    GeoFileUploadPlannedRideContract,
    GeoFileUploadTrackedRideContract,
    GeoFileUploadWaypointContract,
    RiderColor,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    ConsoleLogService,
    GeoCoordinate,
} from '@polaris-customerweb/sharedcomponents.shared';
import {
    Geofence,
    PlannedRide,
    PlannedRideTrack,
    TrackedRide,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import {
    UserProfile,
    UserProfileService,
} from '@polaris-customerweb/sharedcomponents.user-profile';
import { Observable, forkJoin, map } from 'rxjs';
import { GeoFileImport } from 'src/app/models/geo-file-import';
import { GeoFileUpload } from 'src/app/models/geo-file-upload';
import { UuidService } from 'src/app/services/uuid.service';
import { GeoFileImportResourceService } from './geo-file-import-resource.service';

@Injectable({ providedIn: 'root' })
export class GeoFileImportService {
    constructor(
        private importResourceService: GeoFileImportResourceService,
        private userProfileService: UserProfileService,
        private uuidService: UuidService,
        private consoleLogService: ConsoleLogService
    ) {}

    uploadGeoFile(file: File): Observable<GeoFileUpload> {
        return forkJoin([
            this.importResourceService.uploadGeoFile(file),
            this.userProfileService.getUserProfile$(),
        ]).pipe(
            map(
                ([featureCollection, profile]: [
                    GeoFileUploadContract,
                    UserProfile
                ]) =>
                    ({
                        collectionName:
                            featureCollection?.collection?.name || '',
                        features: featureCollection.geoJson.features.map(
                            (feature, index) => {
                                if (
                                    this.uuidService.isEmpty(
                                        feature.properties.id || ''
                                    )
                                ) {
                                    feature.properties.id = `preview-${String(
                                        index + 1
                                    )}`;
                                }

                                const defaultColor: RiderColor =
                                    profile.riderColor ?? 'Blue';
                                switch (feature.properties.entityType) {
                                    case 'PlannedRide':
                                        return this.mapPlannedRide(
                                            feature as GeoFileUploadPlannedRideContract,
                                            defaultColor
                                        );
                                    case 'TrackedRide':
                                        return this.mapTrackedRide(
                                            feature as GeoFileUploadTrackedRideContract,
                                            defaultColor
                                        );
                                    case 'Geofence':
                                        return this.mapGeofence(
                                            feature as GeoFileUploadGeofenceContract,
                                            defaultColor
                                        );
                                    default:
                                        return this.mapWaypoint(
                                            feature as GeoFileUploadWaypointContract,
                                            defaultColor
                                        );
                                }
                            }
                        ),
                        sessionId: featureCollection.sessionId,
                    } as GeoFileUpload)
            )
        );
    }

    importGeoFile(geoImport: GeoFileImport): Observable<void> {
        return this.importResourceService.importGeoFile(geoImport);
    }

    getImportStatus(
        sessionId: string
    ): Observable<GeoFileImportStatusContract> {
        return this.importResourceService.getImportStatus(sessionId);
    }

    private mapPlannedRide(
        feature: GeoFileUploadPlannedRideContract,
        defaultColor: RiderColor
    ): PlannedRide {
        const { featureCollection, ...otherProperties } = feature.properties;
        this.consoleLogService.debug(featureCollection);

        const plannedRide = new PlannedRide({
            featureCollection: {
                features: [
                    {
                        geometry: {
                            coordinates: feature.geometry.coordinates,
                            type: 'LineString',
                        },
                        properties: {
                            featureType: 'PlannedTrack',
                        },
                        type: 'Feature',
                    },
                    {
                        geometry: {
                            coordinates: feature.geometry.coordinates[0],
                            type: 'Point',
                        },
                        properties: {
                            featureType: 'Waypoint',
                        },
                        type: 'Feature',
                    },
                ],
                type: 'FeatureCollection',
            },
            ...otherProperties,
        });
        plannedRide.ride = plannedRide.features[0] as PlannedRideTrack;
        plannedRide.ride.setColor(feature.properties.color || defaultColor);
        return plannedRide;
    }

    private mapTrackedRide(
        feature: GeoFileUploadTrackedRideContract,
        defaultColor: RiderColor
    ): TrackedRide {
        const trackedRideDto = feature;
        trackedRideDto.properties.totalLengthMeters =
            trackedRideDto.properties.totalLengthMeters ||
            feature.properties.totalDistance ||
            0;
        if (feature.properties?.rideEventTimestamps?.[1]?.timestamp) {
            const start = new Date(
                feature.properties.rideEventTimestamps[0].timestamp
            );
            const end = new Date(
                feature.properties.rideEventTimestamps[1].timestamp
            );
            const diff = end.getTime() - start.getTime();
            trackedRideDto.properties.totalDurationSeconds =
                diff > 0 ? diff / 1000 : 0;
        }
        trackedRideDto.properties.color =
            feature.properties.color || defaultColor;
        return new TrackedRide(trackedRideDto);
    }

    private mapWaypoint(
        feature: GeoFileUploadWaypointContract,
        defaultColor: RiderColor
    ): Waypoint {
        const waypointDto = feature.properties;
        waypointDto.publicstatus = Waypoint.publishedState.private;
        if (!(waypointDto.loc?.lat || waypointDto.loc?.lon)) {
            const geoCoordinate = GeoCoordinate.fromCoordinateArray(
                feature.geometry.coordinates
            );

            waypointDto.loc = {
                lat: geoCoordinate.latitude,
                lon: geoCoordinate.longitude,
            };
        }
        waypointDto.tintForTagIcon = waypointDto.tintForTagIcon || defaultColor;
        return new Waypoint(waypointDto);
    }

    private mapGeofence(
        feature: GeoFileUploadGeofenceContract,
        defaultColor: RiderColor
    ): Geofence {
        return new Geofence({
            ...feature.properties,
            featureCollection: {
                features: [
                    {
                        geometry: feature.geometry,
                        properties: {
                            ...feature.properties,
                            color: feature.properties.color || defaultColor,
                        },
                        type: 'Feature',
                    },
                ],
                type: 'FeatureCollection',
            },
        });
    }
}
